import { useEffect, useMemo, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { BuddyListEntry } from "@shared-core/data/adapter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import { Badge } from "../app/components/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { X, Plus, AlertTriangle, ArrowRight } from "lucide-react";

// Buddies admin (csr-ticket-routing) — manager/admin only. Table +
// inline chips (option A from journey iteration), not a graph view: at
// this team size a table already shows every relationship, and the two
// "weak link" indicators below (no cover configured, one-way cover) give
// the same "where's the gap" signal a graph would, without a layout
// engine. Revisit only if the roster grows large enough that a table
// stops reading clearly — see docs/features/csr-ticket-routing/01_product_brief.md §15b.
// The underlying buddy-list concept is role-agnostic; only today's
// consumer (CSR ticket absence-cover) scopes the candidate roster to CSRs.
export function SettingsBuddiesPage() {
  const dataAdapter = useDataAdapter();
  const [entries, setEntries] = useState<BuddyListEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [pendingBuddyId, setPendingBuddyId] = useState<string>("");

  const load = async () => {
    setLoading(true);
    const result = await dataAdapter.listBuddyLists();
    setEntries(result);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameById = useMemo(
    () => new Map(entries.map((e) => [e.userId, e.userName])),
    [entries],
  );

  // One-way check: A lists B as a buddy, but B's own list doesn't include
  // A back — cover only flows one direction today. Cheap to compute from
  // what's already loaded; no extra query needed.
  const isOneWay = (fromUserId: string, buddyUserId: string): boolean => {
    const buddyEntry = entries.find((e) => e.userId === buddyUserId);
    return !buddyEntry?.buddyUserIds.includes(fromUserId);
  };

  const handleRemoveBuddy = async (entry: BuddyListEntry, buddyId: string) => {
    const next = entry.buddyUserIds.filter((id) => id !== buddyId);
    const updated = await dataAdapter.setBuddyList(entry.userId, next);
    setEntries((prev) =>
      prev.map((e) => (e.userId === entry.userId ? updated : e)),
    );
  };

  const handleAddBuddy = async (entry: BuddyListEntry) => {
    if (!pendingBuddyId) return;
    const next = [...entry.buddyUserIds, pendingBuddyId];
    const updated = await dataAdapter.setBuddyList(entry.userId, next);
    setEntries((prev) =>
      prev.map((e) => (e.userId === entry.userId ? updated : e)),
    );
    setAddingFor(null);
    setPendingBuddyId("");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl mb-2">Buddies</h1>
        <p className="text-sm text-muted-foreground">
          Who a manager can assign to cover someone's tickets while they're away
          (per BambooHR absence data). Anyone can have a buddy list — today this
          only backs manual covering assignment for CSR/AM job-alert tickets.
          Manager/admin only — people don't edit their own or others' buddy
          lists.
        </p>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Person</TableHead>
              <TableHead>Buddies</TableHead>
              <TableHead className="w-16">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  No one found
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => {
                const candidateBuddies = entries.filter(
                  (e) =>
                    e.userId !== entry.userId &&
                    !entry.buddyUserIds.includes(e.userId),
                );
                return (
                  <TableRow key={entry.userId}>
                    <TableCell className="font-medium align-top">
                      {entry.userName}
                    </TableCell>
                    <TableCell className="align-top">
                      {entry.buddyUserIds.length === 0 ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="size-3" />
                          No cover configured
                        </Badge>
                      ) : (
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {entry.buddyUserIds.map((buddyId) => {
                            const oneWay = isOneWay(entry.userId, buddyId);
                            return (
                              <Badge
                                key={buddyId}
                                variant="outline"
                                className="gap-1 pr-1"
                                title={
                                  oneWay
                                    ? `${nameById.get(buddyId) ?? buddyId} doesn't list ${entry.userName} back — cover only flows one direction`
                                    : undefined
                                }
                              >
                                {oneWay && (
                                  <ArrowRight className="size-3 text-amber-600" />
                                )}
                                {nameById.get(buddyId) ?? buddyId}
                                <button
                                  onClick={() =>
                                    handleRemoveBuddy(entry, buddyId)
                                  }
                                  className="hover:text-destructive"
                                  title="Remove buddy"
                                >
                                  <X className="size-3" />
                                </button>
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                      {addingFor === entry.userId && (
                        <div className="flex items-center gap-2 mt-2">
                          <Select
                            value={pendingBuddyId}
                            onValueChange={setPendingBuddyId}
                          >
                            <SelectTrigger className="w-[200px] h-8 text-sm">
                              <SelectValue placeholder="Choose a person" />
                            </SelectTrigger>
                            <SelectContent>
                              {candidateBuddies.map((c) => (
                                <SelectItem key={c.userId} value={c.userId}>
                                  {c.userName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            onClick={() => handleAddBuddy(entry)}
                            disabled={!pendingBuddyId}
                          >
                            Add
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setAddingFor(null);
                              setPendingBuddyId("");
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {addingFor !== entry.userId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setAddingFor(entry.userId)}
                          disabled={candidateBuddies.length === 0}
                          title={
                            candidateBuddies.length === 0
                              ? "No one else available to add"
                              : "Add a buddy"
                          }
                        >
                          <Plus className="size-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
